// One-tap UAT check-in ("Did Ace get this right?", "Does this match how your
// team works?"). A good answer sends straight away; anything else opens
// optional reason chips and a note. Asked once per thing (`about`).
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { loadFeedback, saveFeedback, type FeedbackSource } from '../../utils/feedback';

export function chipClass(active: boolean) {
  return `text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
    active ? 'bg-teal-500 border-teal-500 text-white' : 'bg-white border-trim text-copy hover:bg-wash'
  }`;
}

interface Props {
  question: string;
  choices: { label: string; good?: boolean }[];
  reasons?: string[];
  prompt: string;
  page: string;
  about: string;
  source: FeedbackSource;
}

export function QuickCheck({ question, choices, reasons = [], prompt, page, about, source }: Props) {
  const { user } = useAuth();
  const [checked, setChecked] = useState(false);
  const [done, setDone] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    let alive = true;
    loadFeedback(user?.username).then(list => {
      if (!alive) return;
      setDone(list.some(e => e.source === source && e.about === about));
      setChecked(true);
    });
    return () => { alive = false; };
  }, [user?.username, source, about]);

  async function send(kind: string) {
    if (!user) return;
    await saveFeedback({ source, kind, reasons: picked, note: note.trim(), page, about });
    setDone(true);
  }

  if (!checked) return null;

  if (done) {
    return <div className="text-xs text-copy">Thanks. Your feedback was submitted.</div>;
  }

  return (
    <div>
      <div className="text-xs font-semibold text-ink">{question}</div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {choices.map(c => (
          <button key={c.label} onClick={() => (c.good ? send(c.label) : setAnswer(c.label))} className={chipClass(answer === c.label)}>
            {c.label}
          </button>
        ))}
      </div>
      {answer && (
        <div className="mt-3">
          {reasons.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {reasons.map(r => (
                <button
                  key={r}
                  onClick={() => setPicked(prev => (prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]))}
                  className={chipClass(picked.includes(r))}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
          <textarea
            value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder={prompt}
            className="w-full px-2.5 py-1.5 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white resize-none"
          />
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-xs text-muted">Optional. Send works either way.</span>
            <button onClick={() => send(answer)} className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white bg-teal-500 hover:bg-teal-600">
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
